import { Button } from "@RHCommerceDev/unstyled-ui-components/Button";
import { Typography } from "@RHCommerceDev/unstyled-ui-components/Typography";
import RHLink from "@RHCommerceDev/component-rh-link";
import { RHFormConsumerTextField } from "@RHCommerceDev/component-rh-text-field/RHFormConsumerTextField";
import {
  EmailField,
  FirstNameField,
  LastNameField
} from "@RHCommerceDev/component-standard-fields";
import TailwindCheckbox from "@RHCommerceDev/component-tailwind-checkbox";
import { usePageContentNoProxy } from "@RHCommerceDev/custom-providers/LocationProvider";
import {
  emailAtom,
  isCreateAccount,
  isTokenExpirationError,
  useSetAuthenticationDialogCurrentScreenAtom
} from "@RHCommerceDev/hooks-atoms";
import useAsync from "@RHCommerceDev/hooks/useAsync";
import { useLocalization } from "@RHCommerceDev/hooks/useLocalization";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import memoize from "@RHCommerceDev/utils/memoize";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { FormControlLabel, Theme, Tooltip, useMediaQuery } from "@mui/material";
import { useStytch, useStytchUser } from "@stytch/react";
import analyticsLoader from "analytics/loader";
import clsx from "clsx";
import { mutationSubscribeToBrands } from "graphql-client/queries/email-subscription";
import {
  createUserMutation,
  getRegisteredUserByEmail
} from "@RHCommerceDev/graphql-client/queries/user";
import useLocale from "hooks-use-locale/useLocale";
import { useCreateAccountTemplate } from "@RHCommerceDev/hooks/useCreateAccountTemplate";
import { useEnv } from "hooks/useEnv";
import { useIsNA } from "@RHCommerceDev/hooks/useIsNA";
import { PasswordEyeIcon } from "icons/PasswordEyeIcon";
import { ShowPassword } from "icons/ShowPasswordIcon";
import { useAtomValue, useSetAtom } from "jotai";
import debounce from "lodash.debounce";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { emailSubscribedCountries } from "resources/countries-config.json";
import { BrandsSubcription } from "utils/analytics/config";
import { PRIVACY_URL } from "utils/constants";
import { getSourceCode } from "utils/getSourceCode";
import { emailRegex } from "utils/regex";
import PasswordIndicator from "../PasswordIndicator";
import RHSpinner from "component-rh-spinner";
import { getClientOrigin } from "@RHCommerceDev/utils/getClientOrigin";
import { isValidatePassword } from "@RHCommerceDev/utils/stytchUtils";
import Cookies from "universal-cookie";

type SignUpFormType = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  join?: boolean;
};

const SignUpWithEmailAndPassword = () => {
  const setAuthenticationDialogCurrentScreen =
    useSetAuthenticationDialogCurrentScreenAtom();
  const env = useEnv();
  const locale = useLocale();
  const { location } = useHistory();
  const eligibleCountries = emailSubscribedCountries;

  const { pageContent } = usePageContentNoProxy();
  const stytchData = pageContent?.emailSignupContent;
  const [errorAlertList, setErrorAlertList] = useState<string[]>([]);

  const prefix = useLocalization();
  const [emailError, setEmailError] = useState(false);
  const setCreateAtom = useSetAtom(isCreateAccount);
  const isNA = useIsNA();
  const templateId = useCreateAccountTemplate();

  const [createUser] = useMutation(createUserMutation, {
    context: {
      fetchOptions: {
        method: "POST"
      }
    }
  });

  const form = useForm<SignUpFormType>({
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: ""
    }
  });

  const stytchClient = useStytch();
  const stytchUser = useStytchUser();
  const isEmailVerified = stytchUser?.user?.emails?.[0]?.verified;
  const country = getCountryFromUrl();
  const validCountries = ["US", "CA", "BE", "DE", "ES", "GB", "FR"];
  const [emailId, setEmailId] = useState("");
  const setEmailValue = useSetAtom(emailAtom);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [password, setPassword] = useState<string>("");
  const [usCaChecked, setUsCaChecked] = useState(true);
  const [otherCountriesChecked, setOtherCountriesChecked] = useState(false);
  const privacy_url = PRIVACY_URL;
  const [errorMessage, setErrorMessage] = useState("");
  const [subscribedBrands, setSubscribedBrands] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isTokenExpirationErrors = useAtomValue(isTokenExpirationError);
  const togglePasswordVisibility = () => {
    setPasswordVisible(prev => !prev);
  };

  const desktopMatchesQuery = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("md")
  );

  const lgScreen = useMediaQuery<Theme>(theme => theme.breakpoints.up("lg"));
  const hostName = processEnvServer
    ? getClientOrigin()
    : window?.location?.origin;

  useEffect(() => {
    if (!isNA) setUsCaChecked(false);
  }, [isNA]);

  const handleCheckboxChange = useCallback(() => {
    if (isNA) {
      setUsCaChecked(prev => !prev);
    } else if (validCountries.includes(country)) {
      setOtherCountriesChecked(prev => !prev);
    }
  }, [country, setUsCaChecked, setOtherCountriesChecked, validCountries]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
  };

  const [subscribeToBrands, { loading: loadingSubmit }] = useMutation(
    mutationSubscribeToBrands,
    {
      context: {
        fetchOptions: {
          method: "POST"
        }
      },

      onError: () => {
        setErrorAlertList([pageContent?.COMMON?.ERROR]);
      }
    }
  );

  const [checkRegisteredUserByEmail] = useLazyQuery(getRegisteredUserByEmail, {
    onCompleted: data => {
      if (data?.getRegisteredUserByEmail?.isRegisteredRhUser) {
        form.clearErrors("email");
        setEmailError(true);
      } else {
        setEmailError(false);
      }
    },
    onError: (error: ApolloError) => {
      const errorType =
        error?.graphQLErrors?.[0]?.extensions?.error_type ||
        error?.networkError?.name;
      // Check for unauthorized credentials error type in onError
      if (errorType === "unauthorized_credentials") {
        form.clearErrors("email");
        setEmailError(true);
      }
    }
  });

  const debouncedCheckUserByEmail = useCallback(
    debounce(async email => {
      if (email) {
        await checkRegisteredUserByEmail({ variables: { email } });
      }
    }, 500),
    [checkRegisteredUserByEmail]
  );

  const handleEmailChange = useCallback(
    e => {
      const enteredEmail = e.target.value;
      setEmailValue(enteredEmail);
      setEmailId(enteredEmail);
      debouncedCheckUserByEmail(enteredEmail);
    },
    [debouncedCheckUserByEmail, setEmailValue]
  );

  const { run, loading } = useAsync(
    async ({ firstName, lastName, email, password }) => {
      setEmailId(email);
      setIsLoading(true);

      try {
        const stytchUser = await stytchClient.passwords.create({
          email,
          password,
          session_duration_minutes: 129600
        });
        await stytchClient.magicLinks.email.send(email, {
          login_magic_link_url: location.pathname.includes(
            "checkout/confirmation"
          )
            ? `${hostName}?redirect_url=${prefix}?type=signup`
            : `${hostName}?redirect_url=${location.pathname}?type=signup`,
          login_expiration_minutes: 1440,
          login_template_id: templateId
        });

        await stytchClient.user.update({
          name: {
            first_name: firstName,
            last_name: lastName
          },
          untrusted_metadata: {
            is2FA: false
          }
        });
        setIsLoading(false);
        setAuthenticationDialogCurrentScreen("CheckYourInbox");
        setCreateAtom(true);

        const { data } = await createUser({
          variables: {
            userInput: {
              firstName,
              lastName,
              email,
              userType: "REGISTERED",
              country: getCountryFromUrl()
            }
          }
        });
        if (!processEnvServer && stytchUser?.user_id && data?.createUser?.id) {
          analyticsLoader(a =>
            a.emitAnalyticsEvent(
              document.querySelector("#spa-root > *")! as HTMLElement,
              a.EVENTS.REGISTER.INT_TYPE,
              {
                userId: data?.createUser?.id,
                loggedIn: "Success",
                signupStatus: "Success"
              }
            )
          );
        }

        setErrorMessage("");
      } catch (error: any) {
        if (!processEnvServer) {
          analyticsLoader(a =>
            a.emitAnalyticsEvent(
              document.querySelector("#spa-root > *")! as HTMLElement,
              a.EVENTS.REGISTER.INT_TYPE,
              {
                loggedIn: "Failure",
                signupStatus: "Failure"
              }
            )
          );
        }
        if (error.error_type === "duplicate_email") {
          setEmailError(true);
        } else if (error.error_type === "invalid_email") {
          form.setError("email", {
            message: stytchData?.Stytch_Error_Object?.invalid_email
          });
        } else if (
          error.error_type === "breached_password" ||
          error.error_type === "weak_password"
        ) {
          form.setError("password", {
            message: stytchData?.Stytch_Error_Object[error.error_type]
          });
        } else {
          console.error("error", error);
        }
      } finally {
        setIsLoading(false); // Ensure loading is turned off only after everything is done
      }
    }
  );

  const onSubmit = useCallback(
    async (formValue: SignUpFormType) => {
      /** PE-7010 make sure the old session is actually cleared */
      const cookies = new Cookies();
      const sessionId = cookies.get("stytch_session");
      if (sessionId) {
        await stytchClient.session.revoke();
      }
      setTimeout(async () => {
        try {
          await run({ firstName, lastName, email, password });
          if (usCaChecked || otherCountriesChecked) {
            let subscribedBrands;
            if (eligibleCountries[country]) {
              subscribedBrands = Object.entries(BrandsSubcription)
                .filter(
                  ([key]) =>
                    key === "RH" ||
                    key === "BC" ||
                    key === "TN" ||
                    key === "OUTLET"
                )
                .map(([key]) => key);
            } else {
              subscribedBrands = Object.entries(BrandsSubcription)
                .filter(([key]) => key === "RH" || key === "OUTLET")
                .map(([key]) => key);
            }

            subscribeToBrands({
              variables: {
                brands: subscribedBrands,
                email,
                country: getCountryFromUrl(),
                locale,
                sourceCode: getSourceCode({
                  place: "EMAIL_SIGN_UP"
                })
              }
            });
          }
        } catch (error) {
          console.error("Submission error:", error);
        }
      }, 500);
      const { firstName, lastName, email, password } = formValue;
    },
    [
      stytchClient.session,
      run,
      usCaChecked,
      otherCountriesChecked,
      eligibleCountries,
      country,
      subscribeToBrands,
      locale
    ]
  );

  const redirect_url = useMemo(() => {
    return `${hostName}${prefix}/trade/membership-application`;
  }, [hostName, prefix]);

  const handleSignIn = useCallback(() => {
    setAuthenticationDialogCurrentScreen("SignInWithEmailAndPassword");
    setEmailValue(emailId);
    setCreateAtom(false);
  }, [emailId, setAuthenticationDialogCurrentScreen]);

  const formValues = form.watch();
  const isFormComplete = Object.values(formValues).every(value => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return false;
  });

  return (
    <div className={`mx-auto flex w-full flex-col gap-4`}>
      <Typography variant={"h1"} className="font-primary-rh-thin">
        {stytchData?.CREATE_AN_ACCOUNT}
      </Typography>
      {isTokenExpirationErrors === true && (
        <Typography
          variant={"error"}
          className="mt-4 overflow-hidden break-words text-center"
        >
          {stytchData?.Your_email_verification}
        </Typography>
      )}
      <Typography
        className={`text-center  font-primary-rhlight  ${
          isTokenExpirationErrors === true ? "mt-2" : ""
        }`}
      >
        {stytchData?.Discover_the_benefits}{" "}
        {desktopMatchesQuery ? <br /> : null}
        {stytchData?.and_seamlessly_manage}
      </Typography>
      <div
        className={`relative w-full ${
          isLoading ? "pointer-events-none opacity-50" : ""
        }`}
      >
        <FormProvider {...form}>
          <form
            className={`flex flex-col items-center ${
              desktopMatchesQuery ? "gap-4" : "gap-0"
            }`}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div
              className={`grid grid-cols-1 ${
                !desktopMatchesQuery ? "gap-4" : "gap-x-4"
              } w-full md:grid-cols-2`}
            >
              <div className={`${desktopMatchesQuery && "h-16"}`}>
                <FirstNameField
                  className="mt-1 flex justify-center [&>p]:mt-0 [&>p]:text-xxsm [&>p]:!text-rh-red"
                  name="firstName"
                  label={stytchData?.First_Name}
                  rules={{ required: stytchData?.First_name_is }}
                  disabled={isLoading}
                />
              </div>
              <div className={`${desktopMatchesQuery && "h-16"}`}>
                <LastNameField
                  className="mt-1 flex justify-center [&>p]:mt-0 [&>p]:text-xxsm [&>p]:!text-rh-red"
                  rules={{ required: stytchData?.Last_name_is }}
                  disabled={isLoading}
                  label={stytchData?.Last_Name}
                />
              </div>
              <div className="">
                <EmailField
                  onChange={handleEmailChange}
                  className="mt-1 flex justify-center [&>p]:mt-0 [&>p]:text-xxsm [&>p]:!text-rh-red"
                  name="email"
                  label={stytchData?.Email}
                  rules={{
                    required: stytchData?.Email_address_is,
                    pattern: {
                      value: emailRegex,
                      message: stytchData?.Stytch_Error_Object?.invalid_email
                    }
                  }}
                  disabled={isLoading}
                />
                {emailError === true && (
                  <Typography
                    variant="error"
                    className={`w-[340px] flex-col items-start justify-start gap-0 font-primary-rhlight text-xxsm tracking-[0.44px]  text-rh-red customMdMax:w-full customMdMax:flex-row ${
                      emailError ? "visible" : "invisible"
                    }`}
                  >
                    {stytchData?.This_Email_address}

                    <p className="flex !items-baseline gap-1">
                      {stytchData?.Please}{" "}
                      <Button
                        className="!h-[15px] cursor-pointer !p-0 !text-xxsm underline decoration-[0.5px] !underline-offset-[3px]"
                        onClick={handleSignIn}
                      >
                        {stytchData?.Sign_In}
                      </Button>{" "}
                      {stytchData?.to_continue}
                    </p>
                  </Typography>
                )}
              </div>

              <div className="relative w-full">
                <Tooltip
                  title={<PasswordIndicator password={password} />}
                  placement={lgScreen ? "right" : "bottom"}
                  arrow
                  open={Boolean(password.length) && desktopMatchesQuery}
                  classes={{
                    tooltip:
                      "bg-white rounded-none shadow-swatchGroupsToolTip p-[20px]",
                    arrow: `!w-4 !h-5 shadow-arrow text-[#fff] before:shadow-swatchGroupsToolTip
                  ${lgScreen ? "left-[-6px] -translate-x-1/2 top-full" : ""} `
                  }}
                >
                  <RHFormConsumerTextField
                    type={passwordVisible ? "text" : "password"}
                    defaultValue=""
                    autoComplete="off"
                    name="password"
                    inputProps={{
                      "data-testid": "password-input",
                      maxLength: 20
                    }}
                    onChange={handlePasswordChange}
                    fullWidth
                    label={stytchData?.Password}
                    className="mt-1 flex justify-center [&>p]:mt-0 [&>p]:text-xxsm [&>p]:!text-rh-red"
                    rules={{ required: stytchData?.PASSWORD_IS_REQUIRED }}
                    disabled={isLoading}
                  />
                </Tooltip>
                <span
                  className="absolute right-3 top-7 -translate-y-1/2 transform cursor-pointer p-0"
                  onClick={togglePasswordVisibility}
                  role="button"
                  onKeyDown={e => {
                    // Ensure the button itself is focused before handling the keydown event
                    if (
                      e.currentTarget === document.activeElement &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault(); // Prevent default behavior
                      togglePasswordVisibility();
                    }
                  }}
                  tabIndex={0}
                >
                  {passwordVisible ? <ShowPassword /> : <PasswordEyeIcon />}
                </span>
              </div>
              {Boolean(password?.length) && !desktopMatchesQuery && (
                <PasswordIndicator password={password} />
              )}
            </div>

            <div
              className={clsx(
                `flex flex-col items-center ${
                  desktopMatchesQuery ? "my-0 gap-[6px]" : "my-6 gap-[8px]"
                }`
              )}
            >
              <FormControlLabel
                style={
                  // had to add inline stylings as class styles are not getting applied
                  desktopMatchesQuery
                    ? { margin: "0px" }
                    : { alignItems: "start", marginLeft: "-7px" }
                }
                control={
                  <TailwindCheckbox
                    className="!h-4 !w-4 before:h-3 before:w-3 "
                    checked={(isNA && usCaChecked) || otherCountriesChecked}
                    onChange={handleCheckboxChange}
                    value="true"
                    onKeyDown={e => {
                      // Ensure the button itself is focused before handling the keydown event
                      if (
                        e.currentTarget === document.activeElement &&
                        (e.key === "Enter" || e.key === " ")
                      ) {
                        e.preventDefault(); // Prevent default behavior
                        handleCheckboxChange();
                      }
                    }}
                    tabIndex={0}
                    svgStyle={{
                      path: "M1.267 4.8 4.32 7.2 8.733.8",
                      viewBox: "0 0 10 8"
                    }}
                    size="small"
                    checkboxLabelClass={clsx("!px-1 !py-0 ", {
                      "top-[-2px]": !desktopMatchesQuery
                    })}
                  />
                }
                label={stytchData?.Join_our_email}
                classes={{
                  label:
                    "text-[11px] !font-300 font-primary-rhlight leading-[16.5px]"
                }}
              />

              <div
                className={`flex w-full flex-col ${
                  desktopMatchesQuery ? "" : "ml-[50px]"
                } ${!desktopMatchesQuery ? "items-start" : "items-center"}`}
              >
                <div className="flex items-center justify-center !gap-1">
                  <Typography className="font-primary-rhlight text-xxsm tracking-[0.44px] text-rh-dark">
                    {stytchData?.I_have_read}
                  </Typography>

                  <RHLink
                    to={privacy_url}
                    underline="always"
                    display="inline"
                    target="_blank"
                  >
                    <Typography className="font-primary-rhlight text-xxsm tracking-[0.44px] text-rh-dark underline decoration-[0.5px] underline-offset-4">
                      {stytchData?.RH_Privacy_Notice}
                    </Typography>
                  </RHLink>
                </div>
              </div>
            </div>

            <Button
              variant="secondary"
              disabled={
                loading ||
                !isFormComplete ||
                !isValidatePassword(password).every(Boolean)
              }
              type="submit"
              className="h-[48px] w-[311px] font-primary-rhlight !text-xxsm !tracking-[0.44px] customMd:w-full customXs:w-full"
            >
              {isLoading ? <RHSpinner /> : stytchData?.CREATE_ACCOUNT}
            </Button>
            <div
              className={`flex items-center justify-center ${
                desktopMatchesQuery ? "" : "mt-4"
              }`}
            >
              <a
                href={redirect_url}
                className="font-primary-rhlight text-[11px] tracking-[0.44px] underline decoration-[0.5px] underline-offset-[3px]"
              >
                {stytchData?.Create_a_Trade}
              </a>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default memoize(SignUpWithEmailAndPassword);
